import React, { useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import { collection, doc, writeBatch } from 'firebase/firestore';
import { db } from '../firebaseConfig';

// Use stable local worker from public/ to guarantee immunity to CDN DLP blocks and ESM module import errors
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.js";

/**
 * Helper to capitalize the very first character of an exercise title.
 * e.g., "dzień dobry ze sztangą na plecach" -> "Dzień dobry ze sztangą na plecach"
 */
const capitalizeFirstLetter = (string) => {
  if (!string || typeof string !== 'string' || string.trim().length === 0) return string;
  const clean = string.trim();
  return clean.charAt(0).toUpperCase() + clean.slice(1);
};

/**
 * Helper to reconstruct clean horizontal text lines from PDF.js item coordinates.
 * Groups items by Y vertical position so words on the same line aren't split onto separate lines.
 */
const extractLinesFromPage = (textContent) => {
  const items = textContent.items.filter(item => item && item.str && item.str.trim().length > 0);
  if (!items.length) return "";

  // Sort primarily by Y descending (top to bottom), secondarily by X ascending (left to right)
  items.sort((a, b) => {
    const yA = a.transform ? Math.round(a.transform[5]) : 0;
    const yB = b.transform ? Math.round(b.transform[5]) : 0;
    const yDiff = yB - yA;
    if (Math.abs(yDiff) > 5) return yDiff;
    const xA = a.transform ? Math.round(a.transform[4]) : 0;
    const xB = b.transform ? Math.round(b.transform[4]) : 0;
    return xA - xB;
  });

  const lines = [];
  let currentLine = "";
  let lastY = null;

  items.forEach(item => {
    const y = item.transform ? Math.round(item.transform[5]) : 0;
    if (lastY !== null && Math.abs(lastY - y) > 5) {
      if (currentLine.trim()) lines.push(currentLine.trim());
      currentLine = item.str.trim();
    } else {
      currentLine += (currentLine ? " " : "") + item.str.trim();
    }
    lastY = y;
  });
  if (currentLine.trim()) lines.push(currentLine.trim());
  return lines.join("\n");
};

/**
 * Advanced Client-Side Parser for GymGhost Training PDFs
 * Extracts Plan Title, Notes, Splits (Dni), Muscle Groups, and Exercise parameters
 */
export const parseTrainingPdfText = (rawText, defaultTitle = "Zaimportowany Plan Treningowy") => {
  const muscleMap = {
    "brzuch": "core", "klatka": "chest", "bicepsy": "biceps", "biceps": "biceps",
    "barki": "shoulders", "bark": "shoulders", "nogi": "legs", "plecy": "back",
    "tricepsy": "triceps", "triceps": "triceps", "łydki": "legs", "core": "core",
    "chest": "chest", "shoulders": "shoulders", "legs": "legs", "back": "back",
    "ramienia": "biceps", "czworogłowe": "legs", "dwugłowe": "legs"
  };

  // Split raw text by "Dzień" segment indicators
  const daySegments = rawText.split(/Dzień\s+(\d+)/i);
  
  // Extract general plan headers from segment 0
  const headerLines = (daySegments[0] || "").trim().split('\n').map(l => l.trim()).filter(l => l.length > 0);
  let planTitle = defaultTitle;
  let planNotes = "";

  if (headerLines.length > 0) {
    // Look for prominent title line in the header
    const candidate = headerLines.slice(0, 2).join(" - ").trim();
    if (candidate && candidate.length > 3 && !/^Dzień/i.test(candidate)) {
      planTitle = candidate;
      planNotes = headerLines.slice(2).join("\n").trim();
    } else {
      planNotes = headerLines.join("\n").trim();
    }
  }

  const parsedRoutines = [];
  const parsedExercises = [];

  // Parse split days
  for (let i = 1; i < daySegments.length; i += 2) {
    const dayNumber = parseInt(daySegments[i], 10) || Math.floor((i + 1) / 2);
    const dayContent = daySegments[i + 1] || "";
    
    const lines = dayContent.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    let currentMuscle = "legs"; // Default fallback
    let currentExercise = null;
    const routineExercises = [];

    lines.forEach(line => {
      const trimmed = line;
      
      // 1. Detect Muscle Group header line (single word or uppercase title like "KLATKA PIERSIOWA", "NOGI")
      const words = trimmed.toLowerCase().replace(/[^a-zęóąśłżźćń\s]/g, '').split(/\s+/).filter(w => w);
      if (words.length <= 3 && words.some(w => muscleMap[w]) && !trimmed.match(/\d+/)) {
        const matchedWord = words.find(w => muscleMap[w]);
        if (matchedWord) {
          currentMuscle = muscleMap[matchedWord];
          return;
        }
      }

      // 2. Clean leading bullets or numbering ("- Wyciskanie" -> "Wyciskanie", "1. Przysiad" -> "Przysiad")
      const hadBullet = /^([-•*–—]|\d+[\)\.])\s*/.test(trimmed);
      let cleanLine = trimmed.replace(/^([-•*–—]|\d+[\)\.])\s*/, '').trim();

      // 3. Check for Sets & Reps patterns (e.g., 4x12, 3x12-15, 3x10, 4 seria x 12, 3x 12)
      const setsRepsRegex = /(\d+)\s*[xX]\s*([\d\-\w\s]+)/;
      const match = cleanLine.match(setsRepsRegex);

      // If the line starts with a bullet/number OR contains a sets/reps multiplier like 3x12, treat it as a movement!
      if (hadBullet || match) {
        let exName = cleanLine;
        let sets = 3;
        let reps = 12;
        let notes = "";

        if (match) {
          sets = parseInt(match[1], 10) || 3;
          const afterSets = match[2].trim();
          const repMatch = afterSets.match(/\d+/g);
          if (repMatch && repMatch.length > 0) {
            reps = parseInt(repMatch[repMatch.length - 1], 10) || 12;
          }
          
          // Exercise name is whatever precedes the sets/reps string
          const matchIdx = cleanLine.indexOf(match[0]);
          if (matchIdx > 0) {
            exName = cleanLine.substring(0, matchIdx).trim();
            notes = cleanLine.substring(matchIdx + match[0].length).trim();
          } else if (matchIdx === 0 && currentExercise) {
            // Line just contained sets/reps without a name on this line, update previous exercise!
            currentExercise.sets = sets;
            currentExercise.reps = reps;
            if (cleanLine.substring(match[0].length).trim()) {
              currentExercise.notes += " " + cleanLine.substring(match[0].length).trim();
            }
            return;
          }
        }

        // Clean up trailing separators, punctuation from exercise name and capitalize first letter
        exName = capitalizeFirstLetter(exName.replace(/[\-:\(\[\]\),;]+$/, '').trim());

        if (exName.length >= 2) {
          if (currentExercise) {
            parsedExercises.push(currentExercise);
            routineExercises.push(currentExercise);
          }
          currentExercise = {
            id: `ex_${currentMuscle}_${Math.random().toString(36).substr(2, 6)}`,
            name: exName,
            targetMuscle: currentMuscle,
            sets: sets,
            reps: reps,
            notes: notes
          };
          return;
        }
      }

      // 4. Append supplementary instructions or hints to the current exercise notes
      if (currentExercise) {
        currentExercise.notes += (currentExercise.notes ? " " : "") + trimmed;
      }
    });

    if (currentExercise) {
      parsedExercises.push(currentExercise);
      routineExercises.push(currentExercise);
    }

    parsedRoutines.push({
      dayNumber,
      dayLabel: `Dzień ${dayNumber}`,
      title: `Plan - Dzień ${dayNumber}`,
      exercises: [...routineExercises]
    });
  }

  return { planTitle, planNotes, parsedRoutines, parsedExercises };
};

export const PdfPlanImporter = ({ trainerId, existingExercises = [], onImportSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [selectedExercises, setSelectedExercises] = useState({});
  const [selectedRoutines, setSelectedRoutines] = useState({});
  const [editablePlanTitle, setEditablePlanTitle] = useState("");
  const [editablePlanNotes, setEditablePlanNotes] = useState("");

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async function () {
        const typedarray = new Uint8Array(this.result);
        const pdf = await pdfjsLib.getDocument(typedarray).promise;
        
        let fullText = "";
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          // Use Y-coordinate line grouping to preserve horizontal associations
          const pageText = extractLinesFromPage(textContent);
          fullText += pageText + "\n";
        }

        const fileNameFallback = file.name ? file.name.replace(/\.pdf$/i, '').replace(/[-_]/g, ' ') : "Zaimportowany Plan Treningowy";
        const data = parseTrainingPdfText(fullText, fileNameFallback);
        
        // Identify which exercises are already registered in the trainer's database
        const existingNamesSet = new Set((existingExercises || []).map(e => (e.name || "").toLowerCase().trim()));
        data.parsedExercises.forEach(ex => {
          const cleanName = (ex.name || "").toLowerCase().trim();
          ex.alreadyExists = existingNamesSet.has(cleanName);
        });

        setParsedData(data);
        setEditablePlanTitle(data.planTitle);
        setEditablePlanNotes(data.planNotes);

        // Auto-select only new exercises that do not already exist in the database
        const initialEx = {};
        data.parsedExercises.forEach(ex => { initialEx[ex.id] = !ex.alreadyExists; });
        setSelectedExercises(initialEx);

        const initialRot = {};
        data.parsedRoutines.forEach((r, idx) => { initialRot[idx] = true; });
        setSelectedRoutines(initialRot);

        setLoading(false);
      };
      reader.readAsArrayBuffer(file);
    } catch (err) {
      console.error("PDF extraction failed:", err);
      alert("Nie udało się odczytać pliku PDF. Upewnij się, że plik nie jest uszkodzony.");
      setLoading(false);
    }
  };

  const handleExecuteImport = async () => {
    if (!parsedData) return;
    setLoading(true);

    try {
      const batch = writeBatch(db);

      // 1. Import Master Catalog Exercises (ONLY newly selected templates: Name and Target Muscle)
      const addedExercises = parsedData.parsedExercises.filter(ex => !ex.alreadyExists && selectedExercises[ex.id]);
      addedExercises.forEach(ex => {
        const docRef = doc(collection(db, "exercises"), ex.id);
        batch.set(docRef, {
          id: ex.id,
          name: ex.name || "Ćwiczenie treningowe",
          targetMuscle: ex.targetMuscle || "legs",
          trainerId: trainerId || "trainer_default",
          createdAt: new Date().toISOString()
        }, { merge: true });
      });

      // 2. Import Routines (where sets, reps, and instructional notes belong)
      const routineMap = {};
      parsedData.parsedRoutines.forEach((routine, idx) => {
        if (!selectedRoutines[idx]) return;

        const routineId = `routine_imported_${Math.random().toString(36).substr(2, 6)}`;
        routineMap[idx] = routineId;

        const docRef = doc(collection(db, "routines"), routineId);
        batch.set(docRef, {
          id: routineId,
          trainerId: trainerId || "trainer_default",
          title: routine.title,
          version: 1,
          exercises: routine.exercises.map(ex => ({
            id: ex.id,
            name: ex.name || "Ćwiczenie treningowe",
            targetMuscle: ex.targetMuscle || "legs",
            sets: ex.sets || 3,
            reps: ex.reps || 12,
            notes: ex.notes || ""
          })),
          createdAt: new Date().toISOString()
        });
      });

      // 3. Import Master Plan
      const planId = `plan_imported_${Math.random().toString(36).substr(2, 6)}`;
      const planDocRef = doc(collection(db, "plans"), planId);
      
      const activeRoutinesForPlan = parsedData.parsedRoutines
        .map((r, idx) => ({ ...r, originalIndex: idx }))
        .filter(r => selectedRoutines[r.originalIndex])
        .map(r => ({
          dayNumber: r.dayNumber,
          dayLabel: r.dayLabel,
          routineId: routineMap[r.originalIndex],
          routineTitle: r.title
        }));

      batch.set(planDocRef, {
        id: planId,
        trainerId: trainerId || "trainer_default",
        title: editablePlanTitle.trim() || "Zaimportowany Plan Treningowy",
        assignedTo: "global",
        assignedToName: "Wszyscy Użytkownicy (Globalnie)",
        notes: editablePlanNotes.trim(),
        createdAt: new Date().toISOString(),
        routines: activeRoutinesForPlan
      });

      await batch.commit();
      alert("🎉 Plan treningowy, jednostki dni oraz baza nowych ćwiczeń zostały pomyślnie zaimportowane!");
      setParsedData(null);
      if (onImportSuccess) onImportSuccess();
    } catch (err) {
      console.error("Import failed:", err);
      alert("Wystąpił błąd zapisu podczas importu: " + err.message);
    }
    setLoading(false);
  };

  return (
    <div className="bg-slate-900/40 border border-slate-800 rounded-3xl p-6 sm:p-8 shadow-xl space-y-7">
      <div className="flex items-center gap-3.5 pb-4 border-b border-slate-800/80">
        <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center shrink-0 shadow-[0_0_15px_rgba(16,185,129,0.2)]">
          <span className="material-symbols-outlined text-emerald-400 text-2xl">picture_as_pdf</span>
        </div>
        <div>
          <h3 className="text-base font-black text-white tracking-tight uppercase m-0">
            Szybki Import Planów PDF
          </h3>
          <p className="text-xs font-medium text-slate-400 mt-1 m-0">
            Wgraj plik PDF z rozpiską podopiecznego, aby automatycznie zaimportować ćwiczenia, dni i plany.
          </p>
        </div>
      </div>

      {/* Upload Box */}
      {!parsedData && (
        <label className="border-2 border-dashed border-slate-750 hover:border-emerald-500/60 bg-slate-950/50 hover:bg-slate-950/80 rounded-2xl p-10 flex flex-col items-center justify-center cursor-pointer transition-all duration-200 group shadow-inner">
          <div className="w-16 h-16 rounded-full bg-emerald-500/5 group-hover:bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-3 transition">
            <span className="material-symbols-outlined text-4xl text-slate-400 group-hover:text-emerald-400 transition">upload_file</span>
          </div>
          <span className="text-sm font-extrabold text-slate-200 group-hover:text-white transition">Kliknij, aby wybrać plik PDF do importu</span>
          <span className="text-[11px] font-semibold text-slate-500 mt-1">Obsługiwany format: pliki .pdf z rozpiskami treningowymi</span>
          <input type="file" accept=".pdf" onChange={handleFileUpload} className="hidden" />
        </label>
      )}

      {loading && (
        <div className="flex items-center justify-center gap-3 p-6 bg-slate-950/40 rounded-2xl border border-slate-800">
          <span className="w-3 h-3 rounded-full bg-emerald-400 animate-ping"></span>
          <p className="text-sm text-emerald-400 font-extrabold m-0">Przetwarzanie struktury PDF...</p>
        </div>
      )}

      {/* Parser Preview & Interactive Validation Suite */}
      {parsedData && !loading && (
        <div className="space-y-7 animate-fadeIn">
          
          {/* Section 1: Interactive Plan Card (Master Cycle) */}
          <div className="border border-emerald-500/40 rounded-2xl p-6 bg-slate-950/80 shadow-[0_0_25px_rgba(16,185,129,0.1)] space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
              <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-emerald-400 text-xl">calendar_month</span>
                <h4 className="text-xs font-black text-emerald-400 uppercase tracking-wider m-0">
                  Wykryty Plan Treningowy (Cykl Główny)
                </h4>
              </div>
              <span className="text-[11px] font-extrabold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-2.5 py-1 rounded-lg">
                Zostanie zapisany w sekcji Plany
              </span>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-[11px] font-extrabold text-slate-400 uppercase mb-1.5 ml-0.5">
                  Nazwa Cyklu / Planu
                </label>
                <input
                  type="text"
                  value={editablePlanTitle}
                  onChange={(e) => setEditablePlanTitle(e.target.value)}
                  placeholder="np. Plan Treningowy - Osiągnięcie Formy"
                  className="w-full bg-slate-900 border border-slate-700 focus:border-emerald-500 text-white font-black text-sm sm:text-base rounded-xl px-4 py-3 outline-none shadow-inner"
                />
              </div>

              {editablePlanNotes !== undefined && (
                <div>
                  <label className="block text-[11px] font-extrabold text-slate-400 uppercase mb-1.5 ml-0.5">
                    Uwagi Ogólne do Planu (Opcjonalnie)
                  </label>
                  <textarea
                    rows={2}
                    value={editablePlanNotes}
                    onChange={(e) => setEditablePlanNotes(e.target.value)}
                    placeholder="Wpisz ogólne zalecenia lub uwagi dotyczące suplementacji / rozgrzewki..."
                    className="w-full bg-slate-900 border border-slate-700 focus:border-emerald-500 text-slate-200 font-medium text-xs sm:text-sm rounded-xl px-4 py-2.5 outline-none shadow-inner resize-y"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 text-xs text-slate-400 pt-1">
              <span className="material-symbols-outlined text-sm text-slate-400">info</span>
              <span>Ten plan automatycznie powiąże wybrane ze struktur poniżej dni treningowe w spójną całość.</span>
            </div>
          </div>

          {/* Section 2: Routines & Days Selection (With full Series, Reps, and Coaching Comments) */}
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h5 className="text-xs font-black text-slate-300 uppercase tracking-widest m-0 flex items-center gap-2">
                <span>Wykryte Dni Treningowe ({parsedData.parsedRoutines.length})</span>
              </h5>
              <span className="text-emerald-400 text-[11px] font-extrabold">
                Zawierają serie, powtórzenia oraz uwagi od trenera
              </span>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {parsedData.parsedRoutines.map((r, idx) => (
                <div key={idx} className="bg-slate-950 p-5 rounded-2xl border border-slate-800 space-y-3.5 shadow">
                  <label className="flex items-center justify-between gap-3 cursor-pointer select-none border-b border-slate-800/80 pb-3">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 shrink-0"></span>
                      <strong className="text-base font-black text-white">{r.title}</strong>
                      <span className="text-[11px] text-slate-300 font-extrabold px-2.5 py-0.5 bg-slate-900 rounded-md border border-slate-800">
                        {r.exercises.length} ćwiczeń w jednostce
                      </span>
                    </div>
                    <input 
                      type="checkbox" 
                      checked={!!selectedRoutines[idx]} 
                      onChange={e => setSelectedRoutines({ ...selectedRoutines, [idx]: e.target.checked })}
                      className="w-5 h-5 rounded-lg bg-slate-900 border-slate-700 text-emerald-500 focus:ring-0 cursor-pointer shrink-0"
                    />
                  </label>

                  <div className="space-y-2 pl-3 sm:pl-4 border-l-2 border-emerald-500/30 my-2">
                    {r.exercises.map((ex, eIdx) => (
                      <div key={eIdx} className="text-xs text-slate-300 flex flex-col sm:flex-row sm:items-center justify-between py-2 border-b border-slate-900/80 last:border-0 gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-black text-white text-sm tracking-tight">{ex.name || "Ćwiczenie treningowe"}</span>
                          <span className="text-[10px] uppercase font-extrabold px-2 py-0.5 rounded bg-slate-850 text-emerald-400 border border-slate-800 shrink-0">
                            {ex.targetMuscle}
                          </span>
                          <span className="text-[11px] bg-indigo-500/10 text-indigo-300 border border-indigo-500/30 px-2.5 py-0.5 rounded-md font-mono font-bold shrink-0 shadow-sm">
                            {ex.sets || 3} serie x {ex.reps || 12} powt.
                          </span>
                        </div>
                        {ex.notes && (
                          <div className="text-[11px] text-slate-300 font-medium italic bg-slate-900/80 px-3 py-1.5 rounded-xl border border-slate-800 text-left sm:text-right max-w-lg leading-relaxed shadow-inner">
                            "{ex.notes}"
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 3: Master Exercise Catalog Templates (Names & Body Parts ONLY) */}
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h5 className="text-xs font-black text-slate-300 uppercase tracking-widest m-0">
                Wykryte Ćwiczenia (Szablony do Katalogu Głównego: {parsedData.parsedExercises.length})
              </h5>
              <span className="text-slate-400 text-[11px] font-bold">
                Nazwa i partia mięśniowa
              </span>
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2 pr-1.5 custom-scrollbar grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {parsedData.parsedExercises.map(ex => {
                const isExisting = ex.alreadyExists || (existingExercises || []).some(existing => (existing.name || "").toLowerCase().trim() === (ex.name || "").toLowerCase().trim());
                return (
                  <label 
                    key={ex.id} 
                    className={`flex items-center justify-between p-3.5 rounded-2xl border transition shadow-sm !m-0 ${
                      isExisting 
                        ? 'bg-slate-900/50 border-slate-800/80 opacity-70 cursor-not-allowed' 
                        : 'bg-slate-950 border-slate-800 hover:border-slate-750 cursor-pointer'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <span className="text-[10px] bg-slate-850 text-emerald-400 px-2.5 py-1 rounded-lg border border-slate-750 uppercase font-black tracking-wider shrink-0">
                        {ex.targetMuscle}
                      </span>
                      <span className="text-xs sm:text-sm text-white font-extrabold tracking-tight">
                        {ex.name || "Ćwiczenie treningowe"}
                      </span>
                      {isExisting && (
                        <span className="text-[10px] bg-amber-500/15 text-amber-300 border border-amber-500/35 px-2 py-0.5 rounded-md font-extrabold uppercase tracking-wider shrink-0 flex items-center gap-1 shadow-sm">
                          <span className="material-symbols-outlined text-xs !m-0 !p-0">bookmark_added</span>
                          <span>Już w bazie</span>
                        </span>
                      )}
                    </div>
                    <input 
                      type="checkbox" 
                      checked={!isExisting && !!selectedExercises[ex.id]} 
                      disabled={isExisting}
                      onChange={e => !isExisting && setSelectedExercises({ ...selectedExercises, [ex.id]: e.target.checked })}
                      className="w-5 h-5 rounded-lg bg-slate-900 border-slate-700 text-emerald-500 focus:ring-0 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer shrink-0 ml-3"
                    />
                  </label>
                );
              })}
            </div>
          </div>

          {/* Action Row */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-slate-800">
            <button 
              type="button" 
              onClick={() => setParsedData(null)} 
              className="!bg-transparent hover:!bg-slate-800 text-xs text-slate-400 hover:text-slate-200 font-extrabold px-5 py-3.5 rounded-xl transition cursor-pointer !m-0 !border !border-slate-700"
            >
              Anuluj
            </button>
            <button 
              type="button" 
              onClick={handleExecuteImport} 
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white text-xs font-black px-6 py-3.5 rounded-xl transition shadow-[0_4px_20px_rgba(16,185,129,0.3)] hover:shadow-[0_6px_25px_rgba(16,185,129,0.45)] cursor-pointer !m-0 !border-0 flex items-center gap-2 active:scale-95"
            >
              <span className="material-symbols-outlined text-lg !m-0 !p-0 leading-none">cloud_upload</span>
              <span>Zaimportuj wybrane pozycje do bazy</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PdfPlanImporter;
